/**
 * shared/protocol/entities.ts — helpers shared by the server snapshot builder (sim/core) and the
 * client (prediction clip boxes, stuck-missile placement), so both sides derive networked entity
 * data with the SAME code [NET §12 pitfall 1].
 *
 * OWNER: NETCODE agent.
 */
import {
  PLAYER_HEIGHT_CROUCH, PLAYER_HEIGHT_PRONE, PLAYER_MAXS_STAND, PLAYER_MINS,
} from '../constants';
import { CONTENTS, type ClipEntity } from '../collision/types';
import { DEG2RAD, dcos, dsin, type ReadonlyVec3, type Vec3 } from '../math';
import { Stance } from '../types';

/** Player hull height per stance [PM §4: stand maxs z 70, crouch 50, prone 30]. */
export function playerHullHeight(stance: Stance): number {
  return stance === Stance.PRONE ? PLAYER_HEIGHT_PRONE : stance === Stance.CROUCH ? PLAYER_HEIGHT_CROUCH : PLAYER_MAXS_STAND[2];
}

/**
 * Fill `out` with the player clip box (Q3 CG_ClipMoveToEntities / SV_LinkEntity of a client):
 * mins (-15,-15,0), maxs (15,15,h(stance)) around the feet origin, CONTENTS_BODY.
 */
export function setPlayerClipEntity(out: ClipEntity, entityNum: number, origin: ReadonlyVec3, stance: Stance): ClipEntity {
  const h = playerHullHeight(stance);
  out.entityNum = entityNum;
  out.absMins[0] = origin[0] + PLAYER_MINS[0]; out.absMins[1] = origin[1] + PLAYER_MINS[1]; out.absMins[2] = origin[2] + PLAYER_MINS[2];
  out.absMaxs[0] = origin[0] + PLAYER_MAXS_STAND[0]; out.absMaxs[1] = origin[1] + PLAYER_MAXS_STAND[1]; out.absMaxs[2] = origin[2] + h;
  out.contents = CONTENTS.BODY;
  out.ownerNum = undefined;
  return out;
}

export function createClipEntity(): ClipEntity {
  return { entityNum: 0, absMins: [0, 0, 0], absMaxs: [0, 0, 0], contents: CONTENTS.BODY };
}

/**
 * Stuck-to-player missile frame [NET §6.2 state 2, §7.4]: a MissileEntityState with
 * state STUCK_PLAYER carries `origin` as an offset in the parent's frame = feet origin, rotated by
 * the parent's view YAW only (Z-up; the body does not pitch). Angles are stored relative too
 * (yaw added). Convert with these two functions on both ends (combat module when sticking,
 * client interpolation when drawing) so a bolt in someone moves with their interpolated body.
 */
export function stuckOffsetToWorld(out: Vec3, offset: ReadonlyVec3, parentOrigin: ReadonlyVec3, parentYawDeg: number): Vec3 {
  const r = parentYawDeg * DEG2RAD;
  const c = dcos(r), s = dsin(r);
  const x = offset[0] * c - offset[1] * s;
  const y = offset[0] * s + offset[1] * c;
  out[0] = parentOrigin[0] + x; out[1] = parentOrigin[1] + y; out[2] = parentOrigin[2] + offset[2];
  return out;
}

export function worldToStuckOffset(out: Vec3, world: ReadonlyVec3, parentOrigin: ReadonlyVec3, parentYawDeg: number): Vec3 {
  const r = parentYawDeg * DEG2RAD;
  const c = dcos(r), s = dsin(r);
  const dx = world[0] - parentOrigin[0], dy = world[1] - parentOrigin[1];
  out[0] = dx * c + dy * s;
  out[1] = -dx * s + dy * c;
  out[2] = world[2] - parentOrigin[2];
  return out;
}
